import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc,
  query, orderBy, serverTimestamp, Timestamp
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../config/firebaseConfig';
import { useUserProfile } from '../../contexts/UserProfileContext';
import useTodoText from './todoText';
import { ALL_STATUSES, PRIORITY_RANK, STATUS_RANK } from './todoColumns';

const toDate = (value) => (value?.toDate ? value.toDate() : value ? new Date(value) : null);

export const isOverdue = (task) => {
  if (!task.dueDate || task.status === 'completed' || task.status === 'cancelled') return false;
  const due = task.dueDate.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
  return due < new Date();
};

export const isAssignedTo = (task, employeeId) => Boolean(employeeId) && (
  task.assigneeId === employeeId || (task.assignees && task.assignees.some((a) => a.id === employeeId))
);

/**
 * Data of Yapılacaklar Listesi: every task (shown to everyone, as before),
 * the active employees for assigning, the automatic CV-update tasks, status
 * changes (with the accounting-team sync), deleting and the list filters.
 * Firestore reads/writes are the old page's.
 */
const useTodoList = () => {
  const { userAuthorization, employeeId } = useUserProfile();
  const { label } = useTodoText();
  const isAdmin = userAuthorization?.includes('admin') || userAuthorization?.includes('Kısıtlı Admin');

  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);
  const [filters, setFilters] = useState({ title: '', priority: '', assignee: '' });
  const [onlyMine, setOnlyMine] = useState(false);
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [selectedTask, setSelectedTask] = useState(null);
  const [savingStatusId, setSavingStatusId] = useState(null);

  // Fetch tasks
  const fetchTasks = useCallback(async () => {
    try {
      setIsLoading(true);
      const tasksRef = collection(db, 'Tasks');
      const q = query(tasksRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const tasksList = snapshot.docs.map(docSnapshot => ({
        id: docSnapshot.id,
        ...docSnapshot.data()
      }));
      setTasks(tasksList);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error(label('toast.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [label]);

  // Fetch employees for assignment dropdown
  const fetchEmployees = useCallback(async () => {
    try {
      const empRef = collection(db, 'Employees');
      const q = query(empRef, orderBy('eName', 'asc'));
      const snapshot = await getDocs(q);
      const empList = snapshot.docs
        .filter(docSnapshot => docSnapshot.data().isActive !== false)
        .map(docSnapshot => ({
          id: docSnapshot.id,
          eName: docSnapshot.data().eName || '',
          eSurname: docSnapshot.data().eSurname || '',
          eMail: docSnapshot.data().eMail || ''
        }));
      setEmployees(empList);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }, []);

  // Özgeçmiş güncelleme görevlerini kontrol et ve oluştur. Görevler artık bir
  // kez okunur (eskiden her çalışan için baştan okunuyordu); her çalışanın
  // kontrolü yalnızca kendi görevlerine baktığı için sonuç aynıdır.
  const checkAndCreateCVUpdateTasks = useCallback(async () => {
    try {
      const today = new Date();
      const threeMonthsAgo = new Date(today);
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

      // Tüm çalışanları getir
      const employeesRef = collection(db, 'Employees');
      const employeesSnapshot = await getDocs(employeesRef);
      const allEmployees = employeesSnapshot.docs
        .filter(docSnapshot => {
          const data = docSnapshot.data();
          // Sadece aktif çalışanlar için görev oluştur
          return data.Active === 1 || data.Active === '1' || data.state === 'aktif';
        })
        .map(docSnapshot => ({
          id: docSnapshot.id,
          ...docSnapshot.data()
        }));

      const tasksSnapshot = await getDocs(query(collection(db, 'Tasks')));
      const allTasks = tasksSnapshot.docs.map(docSnapshot => ({ id: docSnapshot.id, ...docSnapshot.data() }));

      for (const employee of allEmployees) {
        const employeeName = `${employee.eName || ''} ${employee.eSurname || ''}`.trim();

        // Bu çalışana ait özgeçmiş güncelleme görevlerini bul
        const existingCVTasks = allTasks.filter(task =>
          task.isCVUpdateTask === true &&
          task.assigneeId === employee.id &&
          task.status !== 'completed' &&
          task.status !== 'cancelled'
        );

        // Eğer aktif bir özgeçmiş güncelleme görevi yoksa, yeni oluştur
        if (existingCVTasks.length === 0) {
          // Son tamamlanan özgeçmiş güncelleme görevini kontrol et
          const completedCVTasks = allTasks
            .filter(task =>
              task.isCVUpdateTask === true &&
              task.assigneeId === employee.id &&
              task.status === 'completed'
            )
            .sort((a, b) => {
              const aDate = a.completedAt?.toDate?.() || new Date(0);
              const bDate = b.completedAt?.toDate?.() || new Date(0);
              return bDate - aDate;
            });

          let shouldCreateTask = false;

          if (completedCVTasks.length === 0) {
            // Hiç özgeçmiş güncelleme görevi tamamlanmamış, yeni görev oluştur
            shouldCreateTask = true;
          } else {
            // Son tamamlanan görev 3 aydan eski mi kontrol et
            const lastCompletedTask = completedCVTasks[0];
            const lastCompletedDate = lastCompletedTask.completedAt?.toDate?.() || new Date(0);

            if (lastCompletedDate < threeMonthsAgo) {
              shouldCreateTask = true;
            }
          }

          if (shouldCreateTask) {
            // Yeni özgeçmiş güncelleme görevi oluştur
            const dueDate = new Date(today);
            dueDate.setDate(dueDate.getDate() + 7); // 1 hafta içinde tamamlanmalı

            const newTask = {
              title: 'Özgeçmiş Güncelleme',
              description: `Lütfen özgeçmişinizi güncelleyiniz. Özgeçmişinizde son 3 aydaki değişiklikleri (yeni projeler, eğitimler, sertifikalar vb.) eklemeyi unutmayınız.`,
              priority: 'medium',
              status: 'pending',
              assigneeId: employee.id,
              assigneeName: employeeName,
              assignees: [{
                id: employee.id,
                name: employeeName,
                email: employee.eMail || ''
              }],
              dueDate: Timestamp.fromDate(dueDate),
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              isCVUpdateTask: true, // Özgeçmiş güncelleme görevi olduğunu işaretle
              autoCreated: true, // Otomatik oluşturulduğunu işaretle
              createdBy: 'system' // Sistem tarafından oluşturuldu
            };

            await addDoc(collection(db, 'Tasks'), newTask);
          }
        }
      }
    } catch (error) {
      console.error('Özgeçmiş güncelleme görevleri oluşturulurken hata:', error);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchEmployees();

    // Sayfa yüklendiğinde özgeçmiş güncelleme görevlerini kontrol et
    checkAndCreateCVUpdateTasks();
    // Yalnızca açılışta (eski sayfadaki gibi)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The selected task follows refreshed data and is dropped once it is gone
  useEffect(() => {
    if (!selectedTask) return;
    const current = tasks.find((task) => task.id === selectedTask.id);
    if (!current) {
      if (!isLoading) setSelectedTask(null);
    } else if (current !== selectedTask) {
      setSelectedTask(current);
    }
  }, [tasks, selectedTask, isLoading]);

  // Filter & sort tasks (everyone sees every task, as before)
  const visibleTasks = useMemo(() => {
    let filtered = [...tasks];

    // Search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(task =>
        (task.title || '').toLowerCase().includes(term) ||
        (task.description || '').toLowerCase().includes(term) ||
        (task.assigneeName || '').toLowerCase().includes(term) ||
        (task.assignees || []).some(a => (a.name || '').toLowerCase().includes(term))
      );
    }

    if (filters.title) {
      const term = filters.title.toLowerCase();
      filtered = filtered.filter(task => (task.title || '').toLowerCase().includes(term));
    }

    // Status filter
    if (statusFilter !== ALL_STATUSES) {
      filtered = filtered.filter(task => task.status === statusFilter);
    }

    // Priority filter
    if (filters.priority) {
      filtered = filtered.filter(task => task.priority === filters.priority);
    }

    // Assignee filter
    if (filters.assignee) {
      filtered = filtered.filter(task =>
        task.assigneeId === filters.assignee ||
        (task.assignees && task.assignees.some(a => a.id === filters.assignee))
      );
    }

    if (onlyMine) filtered = filtered.filter(task => isAssignedTo(task, employeeId));
    if (onlyOverdue) filtered = filtered.filter(isOverdue);

    // Sort (priority and status in their natural order)
    if (sortConfig.key) {
      const valueOf = (task) => {
        if (sortConfig.key === 'priority') return PRIORITY_RANK[task.priority] || 0;
        if (sortConfig.key === 'status') return STATUS_RANK[task.status] || 0;
        const value = task[sortConfig.key];
        if (value?.toDate) return value.toDate();
        return sortConfig.key === 'title' ? String(value || '').toLocaleLowerCase('tr-TR') : value;
      };
      filtered.sort((a, b) => {
        const aVal = valueOf(a);
        const bVal = valueOf(b);
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [tasks, searchTerm, filters, statusFilter, onlyMine, onlyOverdue, sortConfig, employeeId]);

  // Stats for all tasks
  const stats = useMemo(() => {
    const base = tasks;
    return {
      total: base.length,
      pending: base.filter(t => t.status === 'pending').length,
      inProgress: base.filter(t => t.status === 'in-progress').length,
      completed: base.filter(t => t.status === 'completed').length,
      cancelled: base.filter(t => t.status === 'cancelled').length,
      overdue: base.filter(isOverdue).length,
      mine: base.filter(t => isAssignedTo(t, employeeId)).length,
    };
  }, [tasks, employeeId]);

  const handleFilter = useCallback((key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({ title: '', priority: '', assignee: '' });
    setSearchTerm('');
    setStatusFilter(ALL_STATUSES);
    setOnlyMine(false);
    setOnlyOverdue(false);
  }, []);

  const canChangeStatus = useCallback((task) => isAdmin || isAssignedTo(task, employeeId), [isAdmin, employeeId]);

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      // Önce mevcut görevi al
      const currentTask = tasks.find(t => t.id === taskId);
      if (!currentTask) {
        toast.error(label('toast.notFound'));
        return;
      }

      // Muhasebe ekibi üyeleri
      const accountingTeam = ['AYGÜL DAĞ CEYLAN', 'ÖZGE YIKIMLAR', 'MELİSA ŞENER'];

      // Bu görevin muhasebe ekibi görevi olup olmadığını kontrol et
      const isAccountingTask = currentTask.isAccountingTask ||
                              currentTask.assignees?.some(assignee =>
                                accountingTeam.some(member =>
                                  assignee.name?.toUpperCase().includes(member.toUpperCase())
                                )
                              );

      // Eğer muhasebe görevi ise ve tamamlandı durumuna geçiyorsa
      if (isAccountingTask && newStatus === 'completed') {
        let relatedAccountingTasks = [];

        // Yeni grup sistemi varsa accountingGroup ile bul
        if (currentTask.accountingGroup) {
          relatedAccountingTasks = tasks.filter(task =>
            task.accountingGroup === currentTask.accountingGroup
          );
        } else {
          // Eski sistem: aynı başlık ve açıklamaya sahip muhasebe görevlerini bul
          relatedAccountingTasks = tasks.filter(task => {
            if (task.id === taskId) return true; // Mevcut görevi dahil et

            // Aynı başlık kontrolü (muhasebe işlemleri kısmını çıkar)
            const currentTitle = currentTask.title.replace(' - Muhasebe İşlemleri', '');
            const taskTitle = task.title.replace(' - Muhasebe İşlemleri', '');
            const isSameTask = taskTitle === currentTitle;

            // Muhasebe ekibi üyesi kontrolü
            const isAccountingMember = task.isAccountingTask ||
                                     task.assignees?.some(assignee =>
                                       accountingTeam.some(member =>
                                         assignee.name?.toUpperCase().includes(member.toUpperCase())
                                       )
                                     );

            return isSameTask && isAccountingMember;
          });
        }

        // Tüm ilişkili görevleri tamamlandı durumuna getir
        const updatePromises = relatedAccountingTasks.map(async (task) => {
          const taskRef = doc(db, 'Tasks', task.id);
          const updateData = {
            status: 'completed',
            updatedAt: serverTimestamp(),
            completedAt: serverTimestamp(),
            syncedBy: currentTask.id, // Hangi görev tarafından senkronize edildiğini kaydet
            syncedAt: serverTimestamp()
          };
          return updateDoc(taskRef, updateData);
        });

        await Promise.all(updatePromises);

        const syncedCount = relatedAccountingTasks.length - 1;
        if (syncedCount > 0) {
          toast.success(label('toast.completedSynced', { count: syncedCount }));
        } else {
          toast.success(label('toast.completed'));
        }
      } else {
        // Normal görev güncelleme
        const taskRef = doc(db, 'Tasks', taskId);
        const updateData = {
          status: newStatus,
          updatedAt: serverTimestamp()
        };
        if (newStatus === 'completed') {
          updateData.completedAt = serverTimestamp();
        }
        await updateDoc(taskRef, updateData);
        toast.success(label('toast.statusUpdated'));
      }

      fetchTasks();
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error(label('toast.statusError'));
    }
  };

  /** handleStatusChange with a busy flag for the control that started it. */
  const changeStatus = async (taskId, newStatus) => {
    if (savingStatusId) return;
    setSavingStatusId(taskId);
    try {
      await handleStatusChange(taskId, newStatus);
    } finally {
      setSavingStatusId(null);
    }
  };

  // Asked first by the page (ConfirmDialog instead of window.confirm)
  const deleteTask = async (taskId) => {
    try {
      await deleteDoc(doc(db, 'Tasks', taskId));
      toast.success(label('toast.deleted'));
      fetchTasks();
      return true;
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error(label('toast.deleteError'));
      return false;
    }
  };

  return {
    isAdmin,
    employeeId,
    tasks,
    employees,
    isLoading,
    visibleTasks,
    stats,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    filters,
    handleFilter,
    onlyMine,
    setOnlyMine,
    onlyOverdue,
    setOnlyOverdue,
    clearFilters,
    sortConfig,
    setSortConfig,
    selectedTask,
    setSelectedTask,
    canChangeStatus,
    changeStatus,
    savingStatusId,
    deleteTask,
    fetchTasks,
    toDate,
  };
};

export default useTodoList;
